import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { ChevronRight, MoreHorizontal } from "lucide-react";
import { cn } from "~/lib/utils";

function Breadcrumb({ ...props }: React.ComponentProps<"nav">) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />;
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<"ol">) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn("flex flex-wrap items-center gap-1", className)}
      {...props}
    />
  );
}

function BreadcrumbItem({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn("inline-flex items-center", className)}
      {...props}
    />
  );
}

function BreadcrumbLink({
  asChild,
  className,
  icon,
  children,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean;
  icon?: React.ReactNode;
}) {
  const Comp = asChild ? Slot : "a";

  return (
    <Comp
      data-slot="breadcrumb-link"
      className={cn(
        "inline-flex cursor-pointer items-center gap-1 rounded-sm px-2",
        "h-[30px]",
        "text-sm font-normal text-muted-foreground",
        "transition-colors duration-200 hover:bg-accent hover:text-foreground",
        className
      )}
      {...props}
    >
      {/* Slot takes a single child, so an `asChild` caller renders its own icon. */}
      {asChild ? (
        children
      ) : (
        <>
          {icon && <span className="flex size-3 items-center justify-center">{icon}</span>}
          {children}
        </>
      )}
    </Comp>
  );
}

function BreadcrumbPage({
  className,
  icon,
  children,
  ...props
}: React.ComponentProps<"span"> & {
  icon?: React.ReactNode;
}) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn(
        "inline-flex items-center gap-1 px-2",
        "h-[30px]",
        "text-sm font-normal text-foreground",
        className
      )}
      {...props}
    >
      {icon && <span className="flex size-3 items-center justify-center">{icon}</span>}
      {children}
    </span>
  );
}

function BreadcrumbSeparator({ children, className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn("text-muted-foreground [&>svg]:size-4", className)}
      {...props}
    >
      {children ?? <ChevronRight />}
    </li>
  );
}

function BreadcrumbEllipsis({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn("flex size-9 items-center justify-center", className)}
      {...props}
    >
      <MoreHorizontal className="size-4" />
      <span className="sr-only">More</span>
    </span>
  );
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
